/**
 * The furniture every working screen is built from.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Nothing here holds a user-facing literal: every string comes from the strings module.
 * Nothing here decides a permission: chips colour the state the server sent, absence
 * renders the absence the predicate produced, and a refusal shows the human text the
 * server wrote.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useOutletContext } from "react-router";
import { createPortal } from "react-dom";

import { ApiError } from "../api/client";
import type { Me } from "../api/me";
import { RecordCrumb, useCrumbFeed } from "../nav/crumbs";
import { validationSummary } from "../lib/serverErrors";
import { strings, term } from "../brand/strings";

/* ------------------------------------------------------------------ */
/* The state chip. One tone per meaning, from the token family only.   */
/* ------------------------------------------------------------------ */

export type ChipTone = "neutral" | "attention" | "success" | "danger";

const ATTENTION_STATES = new Set([
  "SUBMITTED",
  "IN_REVIEW",
  "OPEN",
  "IN_PUNCH_LIST",
  "AWAITING_RESPONSE",
  "RESPONSE_PROPOSED",
  "RESULTS_SUBMITTED",
  "IN_ACTION",
  "REQUESTED",
  "DOCUMENTS_REQUESTED",
  "EFFECTIVENESS_REVIEW",
  "ACCEPTED_WITH_COMMENTS",
]);

const SUCCESS_STATES = new Set([
  "APPROVED",
  "ENDORSED",
  "DISTRIBUTED",
  "AGREED",
  "IMPLEMENTED",
  "ACKNOWLEDGED",
  "CLOSED",
  "RECORDED",
  "PUNCH_COMPLETED",
  "ACCEPTED",
  "DOCUMENTS_APPROVED",
  "FINAL_APPROVAL",
  "CONFIRMED",
]);

const DANGER_STATES = new Set(["REJECTED", "NOT_EFFECTIVE"]);

export function chipTone(state: string | null): ChipTone {
  if (state === null) {
    return "neutral";
  }
  if (DANGER_STATES.has(state)) {
    return "danger";
  }
  if (SUCCESS_STATES.has(state)) {
    return "success";
  }
  if (ATTENTION_STATES.has(state)) {
    return "attention";
  }
  return "neutral";
}

export function StateChip({
  state,
  label,
}: {
  state: string | null;
  label?: string;
}): React.JSX.Element {
  const tone = chipTone(state);
  const toneClass = tone === "neutral" ? "" : ` q-chip--${tone}`;
  return (
    <span className={`q-chip${toneClass}`} data-testid="state-chip" data-tone={tone}>
      {label ?? term(state)}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* The register column header (M5 P5c, his #7): sorting and filtering  */
/* live inside the header, not in a standalone dropdown row.           */
/* ------------------------------------------------------------------ */

/** One filter choice inside a column header. */
export interface HeaderFilterOption {
  readonly value: string;
  readonly label: string;
}

export interface HeaderFilter {
  /** The active tokens of the set; an empty set means "all". */
  readonly values: readonly string[];
  readonly options: readonly HeaderFilterOption[];
  /** Toggle membership: receives the next set; an empty array clears the filter. */
  readonly onChange: (next: readonly string[]) => void;
}

/**
 * The URL carries a column's filter set comma-joined; the empty string is "all".
 * (M5 P6 seat V #6/#8: multi-select within one column.)
 */
export function filterSet(param: string | null): string[] {
  if (param === null) {
    return [];
  }
  return param.split(",").filter((one) => one.length > 0);
}

/** The inverse of `filterSet`: the URL param value for a filter set. */
export function filterParam(values: readonly string[]): string {
  return values.join(",");
}

/** One sortable column's current sort, shared by every header on the table. */
export interface HeaderSort {
  readonly key: string | null;
  readonly dir: "asc" | "desc";
}

/**
 * A register header cell: the column label as a sort toggle when the column is
 * sortable, and — when the column maps to a filterable axis — a chevron menu that
 * carries the filter as a MULTI-SELECT checkbox list, the current selection
 * check-marked (M5 P6 #6/#8). The menu renders in a fixed layer portaled to the
 * document body, so a shrunken register's vertical scroll can never trap it, and
 * toggling an option keeps the menu open so several can be chosen together.
 * URL query params stay the source of truth; the screens hand in the current values.
 */
export function RegisterTh({
  label,
  sortKey,
  sort,
  onSort,
  filter,
}: {
  label: string;
  /** Set when the column sorts; the key the URL carries. */
  sortKey?: string;
  sort?: HeaderSort;
  onSort?: (key: string) => void;
  filter?: HeaderFilter;
}): React.JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAt, setMenuAt] = useState({ top: 0, left: 0 });
  // The open menu's element: outside clicks are judged against it (spec 2.7).
  const menuRef = useRef<HTMLDivElement | null>(null);
  const sorted = sort !== undefined && sortKey !== undefined && sort.key === sortKey;
  const arrow = sorted ? (sort.dir === "asc" ? " ▲" : " ▼") : "";
  const testid = sortKey ?? (filter !== undefined ? `filter-${label}` : undefined);
  const openMenu = (anchor: HTMLElement): void => {
    const box = anchor.getBoundingClientRect();
    setMenuAt({
      top: box.bottom + 4,
      left: Math.max(8, box.left - 96),
    });
    setMenuOpen(true);
  };
  // Dismissal (spec 2.7): outside click, Escape, and scroll-away. The scroll listener
  // is capture-phase so a scroll inside the register's own viewport closes the menu
  // too, even though the event never bubbles to the window.
  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (menuRef.current === null || !(target instanceof Node)) {
        setMenuOpen(false);
        return;
      }
      if (!menuRef.current.contains(target)) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };
    const onScroll = (): void => {
      setMenuOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [menuOpen]);
  return (
    <th scope="col" data-testid={testid === undefined ? undefined : `th-${testid}`}>
      {sortKey !== undefined && onSort !== undefined ? (
        <button
          type="button"
          className="q-th__sort"
          onClick={() => {
            onSort(sortKey);
          }}
        >
          {label}
          {arrow}
        </button>
      ) : (
        label
      )}
      {filter !== undefined && (
        <>
          <button
            type="button"
            className={`q-th__chevron${filter.values.length > 0 ? " q-th__chevron--active" : ""}`}
            aria-label={`${label} filter`}
            aria-expanded={menuOpen}
            data-testid={`filter-toggle-${testid ?? label}`}
            onClick={(event) => {
              const anchor = event.currentTarget;
              if (menuOpen) {
                setMenuOpen(false);
              } else {
                openMenu(anchor);
              }
            }}
          >
            {filter.values.length > 0 ? `${String(filter.values.length)} ▾` : "▾"}
          </button>
          {menuOpen &&
            createPortal(
              <div
                className="q-th__options"
                data-testid={`filter-menu-${testid ?? label}`}
                ref={menuRef}
                style={{ position: "fixed", top: menuAt.top, left: menuAt.left }}
              >
                <button
                  type="button"
                  className="q-th__option"
                  onClick={() => {
                    setMenuOpen(false);
                    filter.onChange([]);
                  }}
                >
                  {strings.findings.all}
                </button>
                {filter.options.map((option) => {
                  const on = filter.values.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={on}
                      className={`q-th__option${on ? " q-th__option--on" : ""}`}
                      onClick={() => {
                        filter.onChange(
                          on
                            ? filter.values.filter((one) => one !== option.value)
                            : [...filter.values, option.value],
                        );
                      }}
                    >
                      {on ? "✓ " : ""}
                      {option.label}
                    </button>
                  );
                })}
              </div>,
              document.body,
            )}
        </>
      )}
    </th>
  );
}

/** The next sort direction for a header click: same key flips, new key starts asc. */
export function nextSort(current: HeaderSort, key: string): HeaderSort {
  if (current.key === key) {
    return { key, dir: current.dir === "asc" ? "desc" : "asc" };
  }
  return { key, dir: "asc" };
}

/** Client-side sort of the loaded rows by a column key's accessor. */
export function sortRows<T>(
  rows: readonly T[],
  sort: HeaderSort,
  accessors: Record<string, (row: T) => string | number | null>,
): T[] {
  if (sort.key === null) {
    return [...rows];
  }
  const read = accessors[sort.key];
  if (read === undefined) {
    return [...rows];
  }
  const factor = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = read(a);
    const vb = read(b);
    if (va === null || vb === null) {
      return va === vb ? 0 : va === null ? 1 : -1;
    }
    if (typeof va === "number" && typeof vb === "number") {
      return (va - vb) * factor;
    }
    return String(va).localeCompare(String(vb)) * factor;
  });
}

/* ------------------------------------------------------------------ */
/* Panels: empty, absence, refusal, loading.                           */
/* ------------------------------------------------------------------ */

/**
 * Wave S: whether the signed-in account is a counterparty account the operator has not
 * yet assigned to any contract. The server says so on /api/me (`assigned_contract_count`
 * is 0); every register such an account opens is empty for that reason alone, and its
 * empty state says so instead of "nothing here".
 */
export function useUnassignedAccount(): boolean {
  const outlet = useOutletContext<{ me?: Me } | null | undefined>();
  return outlet?.me?.assignedContractCount === 0;
}

export function NoAssignmentState({
  testId = "no-assignment-state",
}: {
  readonly testId?: string;
}): React.JSX.Element {
  return (
    <div className="q-empty" role="note" data-testid={testId}>
      <p className="q-empty__title">{strings.common.noAssignmentTitle}</p>
      <p className="q-empty__body">{strings.common.noAssignmentBody}</p>
    </div>
  );
}

export function EmptyState({
  title,
  body,
  assignmentAware = true,
}: {
  title: string;
  /** Omitted when the title already says it all; the sentence then renders once. */
  body?: string;
  /**
   * When the account has no contract assigned, the empty state names that cause instead
   * (Wave S). False where the page already says it once (the Home).
   */
  assignmentAware?: boolean;
}): React.JSX.Element {
  const unassigned = useUnassignedAccount();
  if (assignmentAware && unassigned) {
    return <NoAssignmentState />;
  }
  return (
    <div className="q-empty" data-testid="empty-state">
      <p className="q-empty__title">{title}</p>
      {body !== undefined && <p className="q-empty__body">{body}</p>}
    </div>
  );
}

/**
 * The absence panel. The server answers a counterparty for a record it may not see
 * exactly as it answers for one that does not exist; this panel is how the screen says
 * that in both cases, and it is what a direct URL to a withheld surface yields.
 */
export function AbsencePanel(): React.JSX.Element {
  return (
    <div className="q-absence" data-testid="absence-panel">
      <p className="q-empty__title">{strings.common.absenceHeading}</p>
      <p className="q-empty__body">{strings.common.absenceBody}</p>
    </div>
  );
}

export function NotFoundPanel(): React.JSX.Element {
  return (
    <div className="q-absence" data-testid="absence-panel">
      <p className="q-empty__title">{strings.common.absenceHeading}</p>
      <p className="q-empty__body">{strings.common.notFoundBody}</p>
    </div>
  );
}

/*
 * Refusal language in plain words (M5 P5c, his #5): no rule codes, no dots, no
 * underscores in the UI. The known rule codes map to a heading that names the action
 * and a body that says what to do next; unknown rules keep the server's own human
 * sentence as the body. The raw rule code goes to the console, where it belongs.
 */

/** One known rule's plain words: a heading naming the action and the next step. */
const REFUSAL_WORDS: Record<string, { readonly heading: string; readonly body?: string }> = {
  // Wave R0: the plan's own gates. The server's sentence names the items, so it stays
  // the body; the heading says what kind of refusal it is in plain words.
  "itp.item_without_intervention": { heading: strings.itps.refusalNoOperatorPointHeading },
  "itp.items_not_accepted": { heading: strings.itps.refusalItemsNotAcceptedHeading },
  // Wave R0 Break 7: the implementation leg. The self approval sentence is the server's
  // own (it differs by record); the two state refusals carry a raw state, so they are
  // said here in plain words instead.
  "workflow.no_self_approval": { heading: strings.findings.refusalSelfApprovalHeading },
  "finding.not_agreed": {
    heading: strings.findings.refusalNotAgreedHeading,
    body: strings.findings.refusalNotAgreedBody,
  },
  "finding.not_implemented": {
    heading: strings.findings.refusalNotImplementedHeading,
    body: strings.findings.refusalNotImplementedBody,
  },
  "negotiation.justification_required": {
    heading: strings.common.refusalJustificationHeading,
    body: strings.common.refusalJustificationBody,
  },
  // Wave INST: the server's sentence names the instance, the request or the days, so it
  // stays the body; the heading says in plain words what is to be fixed.
  "rfi.instance_label_required": { heading: strings.itps.refusalInstanceLabelHeading },
  "rfi.already_requested": { heading: strings.itps.refusalAlreadyRequestedHeading },
  "rfi.instance_settled": { heading: strings.itps.refusalInstanceSettledHeading },
  "rfi.short_notice": { heading: strings.itps.refusalShortNoticeHeading },
  "rfi.evidence_unknown": { heading: strings.itps.refusalEvidenceUnknownHeading },
  // Wave INST ruling 2: the completion act. Each sentence names the item, its instances
  // or its counts, so the server's words stay the body.
  "DM-ITP-1": { heading: strings.itps.refusalHoldOrderHeading },
  "itp_item.declared_below_known": { heading: strings.itps.refusalItemCompletionHeading },
  "itp_item.already_declared": { heading: strings.itps.refusalItemCompletionHeading },
  "itp_item.not_attended": { heading: strings.itps.refusalItemCompletionHeading },
  "itp_item.not_declared": { heading: strings.itps.refusalItemCompletionHeading },
  "itp_item.instances_open": { heading: strings.itps.refusalItemCompletionHeading },
  "itp_item.instances_short": { heading: strings.itps.refusalItemCompletionHeading },
  "itp_item.already_cleared": { heading: strings.itps.refusalItemCompletionHeading },
  "itp_item.self_concurrence": { heading: strings.findings.refusalSelfApprovalHeading },
  // Wave RFI rulings 1 to 4 (b12 frontend item 7): the request's shape, its category, the
  // free request's scope and the one assignee. The server's sentence stays the body.
  "rfi.items_required": { heading: strings.itps.refusalItemsHeading },
  "rfi.items_too_many": { heading: strings.itps.refusalItemsHeading },
  "rfi.items_duplicate": { heading: strings.itps.refusalItemsHeading },
  "rfi.items_and_item": { heading: strings.itps.refusalItemsHeading },
  "rfi.items_cross_plan": { heading: strings.itps.refusalItemsHeading },
  "rfi.checklist_category_unknown": { heading: strings.itps.refusalCategoryHeading },
  "rfi.free_rfi_engineering_only": { heading: strings.itps.refusalFreeEngineeringHeading },
  "rfi.not_assignee": { heading: strings.rfis.refusalNotAssigneeHeading },
  "rfi.assignee_not_eligible": { heading: strings.rfis.refusalAssignHeading },
  "rfi.already_assigned": { heading: strings.rfis.refusalAssignHeading },
  "rfi.assignee_locked": { heading: strings.rfis.refusalAssignHeading },
  // Wave CRED rulings 15 to 20 (b12 frontend item 9): credentials, measured values, the
  // classification of a file and the staffing rules. The server's sentence is the body.
  "conduct.measurement_out_of_tolerance": { heading: strings.credentials.refusalPassHeading },
  "measurement.contract_state": { heading: strings.credentials.refusalMeasurementHeading },
  "measurement.unit_required": { heading: strings.credentials.refusalMeasurementHeading },
  "measurement.tolerance_order": { heading: strings.credentials.refusalMeasurementHeading },
  "credential.expired": { heading: strings.credentials.refusalMeasurementHeading },
  "evidence.contract_state": { heading: strings.credentials.refusalEvidenceHeading },
  "evidence.inspection_body_required": { heading: strings.credentials.refusalEvidenceHeading },
  "evidence.unchanged": { heading: strings.credentials.refusalEvidenceHeading },
  "staffing.contract_state": { heading: strings.credentials.refusalStaffingHeading },
  "staffing.rule_exists": { heading: strings.credentials.refusalStaffingHeading },
  "credential.reason_required": { heading: strings.credentials.refusalHeading },
  "credential.unchanged": { heading: strings.credentials.refusalHeading },
  "credential.revoked": { heading: strings.credentials.refusalHeading },
  "credential.already_recorded": { heading: strings.credentials.refusalHeading },
  "credential.validity_required": { heading: strings.credentials.refusalHeading },
  "credential.validity_order": { heading: strings.credentials.refusalHeading },
  "credential.evidence_required": { heading: strings.credentials.refusalHeading },
  "credential.holder_required": { heading: strings.credentials.refusalHeading },
  // Wave STD ruling 12 (b12 frontend item 8): a citation added or removed.
  "citation.contract_state": { heading: strings.standards.refusalHeading },
  "citation.already_cited": { heading: strings.standards.refusalHeading },
  "standards.edition_withdrawn": { heading: strings.standards.refusalHeading },
  // Wave COM item 3: the release of an unattended witness point. The server's sentence
  // names the deadline (rfi.witness_not_released) or the hold point; it stays the body.
  "rfi.witness_not_released": { heading: strings.rfis.refusalWitnessHeading },
  "rfi.hold_point_never_released": { heading: strings.rfis.refusalWitnessHeading },
  "rfi.witness_already_released": { heading: strings.rfis.refusalWitnessHeading },
  "rfi.release_declaration_required": { heading: strings.rfis.refusalWitnessHeading },
  // Wave INST ruling 11: the server's sentence names the existing rating.
  "criticality.line_already_rated": { heading: strings.criticality.refusalLineRatedHeading },
  "PL-STATUS": {
    heading: strings.common.refusalHeading,
    body: strings.findings.punchListClosedRefusal,
  },
  // Wave AUD item 2: the audit register's range gate. The server's sentence stays the body.
  "audit.range_empty": { heading: strings.audit.refusalRangeEmptyHeading },
};

export function RefusalBox({ error }: { error: ApiError }): React.JSX.Element | null {
  if (error.kind !== "conflict" && error.kind !== "forbidden" && error.kind !== "invalid") {
    return null;
  }
  const rule = error.problem?.rule ?? null;
  const words = rule === null ? undefined : REFUSAL_WORDS[rule];
  // The rule code is for logs only; the screen renders plain words.
  if (rule !== null) {
    console.warn(`refusal rule: ${rule}`);
  }
  // Wave W1.5 (finding 2): a validation refusal names its fields in plain words; each
  // control under a ServerErrorScope also marks its own field (lib/serverErrors).
  const summary = validationSummary(error, strings.fieldNames);
  const rawMessage = error.message;
  const cleanMessage = rawMessage.replace(/\[[A-Za-z0-9_.-]+\]\s*/g, "");
  return (
    <div className="q-refusal" role="alert" data-testid="refusal" data-rule={rule ?? undefined}>
      <p className="q-refusal__heading">
        {summary !== null
          ? strings.common.validationHeading
          : (words?.heading ?? strings.common.refusalHeading)}
      </p>
      <p className="q-thread__body">{summary ?? words?.body ?? cleanMessage}</p>
    </div>
  );
}

/**
 * Wave INST ruling 3: the request was raised inside its type's notice lead time, with a
 * justification. Flagged for the operator wherever the request is listed.
 */
export function ShortNoticeChip({
  rfi,
}: {
  readonly rfi: { readonly shortNotice: boolean };
}): React.JSX.Element | null {
  if (!rfi.shortNotice) {
    return null;
  }
  return (
    <span
      className="q-chip q-chip--attention"
      data-testid="rfi-short-notice"
      title={strings.rfis.shortNoticeTitle}
    >
      {strings.rfis.shortNotice}
    </span>
  );
}

export function LoadingLine({ label }: { label: string }): React.JSX.Element {
  return (
    <p className="q-status" role="status">
      {label}
    </p>
  );
}

/* ------------------------------------------------------------------ */
/* Breadcrumbs (M5 P6 seat V #7). Every detail screen opens with the   */
/* path back: each crumb a link to its register, the current page plain.*/
/* ------------------------------------------------------------------ */

export interface Crumb {
  readonly label: string;
  /** Set for every crumb except the current page. */
  readonly to?: string;
}

/**
 * Wave W2: inside the shell there is ONE trail, the shell's (nav/crumbs.tsx), on every
 * screen. A page's own trail no longer draws: it NAMES its record on the shell's trail
 * (the last crumb, when the one before it is a register). Drawn outside the shell (a
 * screen rendered on its own), it draws itself as before.
 */
export function Breadcrumbs({ trail }: { trail: readonly Crumb[] }): React.JSX.Element | null {
  const feed = useCrumbFeed();
  if (feed !== null) {
    const last = trail[trail.length - 1];
    const parent = trail[trail.length - 2];
    const names =
      last !== undefined && last.to === undefined && parent?.to !== undefined && parent.to !== "/";
    return names ? <RecordCrumb label={last.label} /> : null;
  }
  return (
    <nav className="q-crumbs" data-testid="breadcrumbs" aria-label="Breadcrumb">
      {trail.map((crumb, index) =>
        crumb.to === undefined ? (
          <span className="q-crumbs__here" key={index} aria-current="page">
            {crumb.label}
          </span>
        ) : (
          <Link className="q-crumbs__link" key={index} to={crumb.to}>
            {crumb.label}
          </Link>
        ),
      )}
    </nav>
  );
}

/* ------------------------------------------------------------------ */
/* Skeletons (M5 P6 seat V #5). While a screen's primary query loads   */
/* it shows token-coloured blocks in the shape of what is coming —     */
/* never raw content that flashes and restyles. Shimmer-free by        */
/* design: the blocks simply stand where the rows and fields stand.    */
/* ------------------------------------------------------------------ */

export function SkeletonRows({
  rows = 8,
  columns = 5,
}: {
  rows?: number;
  columns?: number;
}): React.JSX.Element {
  return (
    <div className="q-skel" data-testid="skeleton-rows" role="status" aria-busy="true">
      {Array.from({ length: rows }, (_, row) => (
        <div className="q-skel__row" key={row}>
          {Array.from({ length: columns }, (_, column) => (
            <div
              className="q-skel__block"
              key={column}
              style={{ width: `${String(55 + ((row + column) % 4) * 10)}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function SkeletonFacts({ fields = 6 }: { fields?: number }): React.JSX.Element {
  return (
    <div className="q-skel" data-testid="skeleton-facts" role="status" aria-busy="true">
      {Array.from({ length: fields }, (_, field) => (
        <div className="q-skel__field" key={field}>
          <div className="q-skel__block q-skel__block--short" />
          <div className="q-skel__block" />
        </div>
      ))}
    </div>
  );
}

export function SkeletonTiles({ count = 4 }: { count?: number }): React.JSX.Element {
  return (
    <div className="q-skel" data-testid="skeleton-tiles" role="status" aria-busy="true">
      {Array.from({ length: count }, (_, tile) => (
        <div className="q-skel__tile" key={tile}>
          <div className="q-skel__block q-skel__block--short" />
          <div className="q-skel__block" />
        </div>
      ))}
    </div>
  );
}

export function SkeletonChart(): React.JSX.Element {
  return (
    <div className="q-skel" data-testid="skeleton-chart" role="status" aria-busy="true">
      <div className="q-skel__tile q-skel__tile--chart">
        <div className="q-skel__block" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Sections and the facts grid.                                        */
/* ------------------------------------------------------------------ */

export function Section({
  heading,
  lede,
  id,
  variant = "default",
  className,
  children,
}: {
  heading: string;
  lede?: string;
  /** An anchor a menu entry may land on (wave W1: the planning candidates). */
  id?: string;
  variant?: "default" | "banded" | "paper";
  className?: string;
  children: ReactNode;
}): React.JSX.Element {
  const variantClass =
    variant === "banded" ? "q-section--banded" : variant === "paper" ? "q-section--paper" : "";
  return (
    <section
      className={`q-section ${variantClass} ${className ?? ""}`.trim()}
      id={id}
      data-variant={variant}
    >
      <h3 className="q-section__heading">{heading}</h3>
      {lede !== undefined && <p className="q-section__lede">{lede}</p>}
      {children}
    </section>
  );
}

export function Facts({ entries }: { entries: [string, ReactNode][] }): React.JSX.Element {
  return (
    <dl className="q-facts">
      {entries.map(([name, value]) => (
        /* The name is a static label from the strings module, never data. */
        <FragmentPerEntry key={name} name={name} value={value} />
      ))}
    </dl>
  );
}

function FragmentPerEntry({ name, value }: { name: string; value: ReactNode }): React.JSX.Element {
  return (
    <>
      <dt>{name}</dt>
      <dd>{value}</dd>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Server pagination for the registers that carry limit and offset.    */
/* ------------------------------------------------------------------ */

export function Pagination({
  total,
  limit,
  offset,
  onMove,
}: {
  total: number;
  limit: number;
  offset: number;
  onMove: (offset: number) => void;
}): React.JSX.Element {
  const page = Math.floor(offset / limit) + 1;
  const pages = Math.max(1, Math.ceil(total / limit));
  return (
    <nav className="q-pagination" aria-label="Pages">
      <span>
        {strings.common.total(total)} {pages > 1 && ` · ${strings.common.page(page, pages)}`}
      </span>
      <span className="q-row">
        <button
          type="button"
          className="q-button q-button--quiet"
          disabled={offset <= 0}
          onClick={() => {
            onMove(Math.max(0, offset - limit));
          }}
        >
          {strings.common.previousPage}
        </button>
        <button
          type="button"
          className="q-button q-button--quiet"
          disabled={offset + limit >= total}
          onClick={() => {
            onMove(offset + limit);
          }}
        >
          {strings.common.nextPage}
        </button>
      </span>
    </nav>
  );
}

/* ------------------------------------------------------------------ */
/* Dates. Rendered as the record carries them, never reformatted into  */
/* something the record did not say.                                   */
/* ------------------------------------------------------------------ */

/**
 * N03 (coherence review, wave W0): a system code never reaches a user-facing cell. A
 * value shaped like a token (UPPER_SNAKE) renders through the known labels, or as plain
 * words; the raw code goes to the console for whoever is debugging (UX_SPEC).
 */
const KNOWN_TOKEN_LABELS: Readonly<Record<string, string>> = {
  OPERATOR_QUALITY_REPRESENTATIVE: "Operator Quality Representative",
};

export function plainLabel(value: string | null | undefined): string {
  if (value === null || value === undefined) {
    return "";
  }
  if (!/^[A-Z][A-Z0-9]*(_[A-Z0-9]+)+$/.test(value)) {
    return value;
  }
  console.debug("qualify: system code rendered as a label", value);
  const known = KNOWN_TOKEN_LABELS[value];
  if (known !== undefined) {
    return known;
  }
  const words = value.toLowerCase().replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function day(value: string | null): string {
  if (value === null || value.length === 0) {
    return "";
  }
  return value.slice(0, 10);
}

export function stamp(value: string | null): string {
  if (value === null || value.length === 0) {
    return "";
  }
  return value.replace("T", " ").slice(0, 16);
}

/* ------------------------------------------------------------------ */
/* The register's text search. The server's list endpoints carry no    */
/* text parameter, so this narrows the loaded page client-side and     */
/* says so in plain words beside the input (NOTES.md, M5 P5b).         */
/* ------------------------------------------------------------------ */

export function PageSearch({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
}): React.JSX.Element {
  return (
    <div className="q-matrix__axis" data-testid="page-search">
      <label className="q-field__label" htmlFor={id}>
        {strings.common.searchLabel}
      </label>
      <input
        id={id}
        className="q-input"
        type="search"
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
      <p className="q-note">{strings.common.searchNote}</p>
    </div>
  );
}
